import React, { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, ExternalLink, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORAGE_KEY = "psytech_domain_banner_dismissed_at";
const DISMISS_HOURS = 12;

/** Banner shown across CRM pages while Resend is in sandbox mode. */
export default function DomainBanner({ compact = false }) {
  const [status, setStatus] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/admin/crm/sender-status`)
      .then(({ data }) => { if (!cancelled) setStatus(data); })
      .catch(() => { /* non-blocking */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    try {
      const ts = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
      if (ts && Date.now() - ts < DISMISS_HOURS * 3600 * 1000) {
        setHidden(true);
      }
    } catch { /* noop */ }
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* noop */ }
    setHidden(true);
  };

  if (!status || !status.configured || !status.sandbox_mode || hidden) return null;

  const verified = status.verified_recipients?.[0];

  return (
    <div
      className={`bg-gradient-to-r from-amber-50 to-orange-50 border ${compact ? "border-amber-200 rounded-md px-3 py-2" : "border-amber-300 rounded-xl px-4 py-3 shadow-sm"} flex items-start gap-3`}
      data-testid="crm-domain-banner"
    >
      <div className={`flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 ring-1 ring-amber-300 flex items-center justify-center ${compact ? "w-6 h-6" : ""}`}>
        <AlertTriangle className={`${compact ? "w-3 h-3" : "w-4 h-4"} text-amber-700`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-amber-900">
          Resend is in sandbox mode — outreach sends will be limited.
        </div>
        <div className="text-xs text-amber-800 mt-0.5 leading-relaxed">
          Until <code className="font-mono bg-white/60 px-1 rounded">psy-tech.nl</code> is verified, only{" "}
          {verified ? (
            <>
              <strong className="font-mono">{verified}</strong> can receive sent emails.
            </>
          ) : (
            "your own verified email can receive sent emails."
          )}{" "}
          Drafts to other contacts will fail in <code className="font-mono">email_logs</code> with status=failed.
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          <a
            href={status.verify_url || "https://resend.com/domains"}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-amber-700 text-white hover:bg-amber-800 font-semibold"
            data-testid="crm-domain-banner-verify"
          >
            Verify domain at Resend <ExternalLink className="w-3 h-3" />
          </a>
          <span className="text-amber-700">
            Sender: <strong className="font-mono">{status.sender_email || "—"}</strong>
            {status.recent_successful_sends > 0 && (
              <span className="ml-2 text-amber-600">· {status.recent_successful_sends} successful sends so far</span>
            )}
          </span>
        </div>
      </div>
      <button
        onClick={dismiss}
        className="flex-shrink-0 text-amber-600 hover:text-amber-900 p-1"
        title="Dismiss for 12 hours"
        data-testid="crm-domain-banner-dismiss"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
